import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
    useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    if (stored.role !== 'super_admin') {
      navigate('/');
    }
  }, [navigate]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`http://127.0.0.1:8000/api/users/${id}`, { headers: getAuthHeaders() })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          navigate('/login');
          throw new Error('Not authorized');
        }
        if (res.status === 404) throw new Error('User not found');
        if (!res.ok) throw new Error('Failed to fetch user');
        return res.json();
      })
      .then(data => {
        setUser(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [id, navigate]);

  const formatRole = (role) => {
    if (!role) return 'No Role';
    return role.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'super_admin':
        return { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' };
      case 'holding_admin':
        return { backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' };
      case 'company_admin':
        return { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' };
      default:
        return { backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' };
    }
  };

  if (initialLoading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading User...</div>;
  if (error) return <div style={{ color: 'red', padding: '20px' }}>Error: {error}</div>;
  if (!user) return null;

  return (
    <div style={{ padding: '40px', maxWidth: '700px', margin: '0 auto' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/users')}
        style={{
          background: 'none',
          border: 'none',
          color: '#2563eb',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: '500',
          padding: '0',
          marginBottom: '20px',
          display: 'inline-block'
        }}
      >
        ← Back to Users
      </button>

      {/* Profile Card */}
      <div style={{
        backgroundColor: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '32px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '28px' }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: '#2563eb',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            fontWeight: '700',
            flexShrink: 0
          }}>
            {user.name ? user.name.charAt(0).toUpperCase() : '?'}
          </div>
          <div>
            <h1 style={{ margin: '0 0 4px 0', fontSize: '1.4rem', fontWeight: '700', color: '#0f172a' }}>{user.name}</h1>
            <span style={{
              ...getRoleBadgeStyle(user.role),
              padding: '4px 12px',
              borderRadius: '999px',
              fontSize: '0.82rem',
              fontWeight: '600',
              display: 'inline-block'
            }}>
              {formatRole(user.role)}
            </span>
          </div>
        </div>

        {/* Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
            <span style={{ width: '140px', fontSize: '0.85rem', fontWeight: '600', color: '#64748b' }}>Email / phone</span>
            <span style={{ fontSize: '0.95rem', color: '#0f172a' }}>{user.email || user.phone || '—'}</span>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
            <span style={{ width: '140px', fontSize: '0.85rem', fontWeight: '600', color: '#64748b' }}>Role</span>
            <span style={{ fontSize: '0.95rem', color: '#0f172a' }}>{formatRole(user.role)}</span>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
            <span style={{ width: '140px', fontSize: '0.85rem', fontWeight: '600', color: '#64748b' }}>Joined</span>
            <span style={{ fontSize: '0.95rem', color: '#0f172a' }}>
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
            </span>
          </div>
          <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
            <span style={{ width: '140px', fontSize: '0.85rem', fontWeight: '600', color: '#64748b' }}>Last Updated</span>
            <span style={{ fontSize: '0.95rem', color: '#0f172a' }}>
              {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : '—'}
            </span>
          </div>
        </div>

        {/* Assigned Companies */}
        {user.companies && user.companies.length > 0 && (
          <div style={{ marginTop: '28px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0' }}>Assigned Companies</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {user.companies.map(company => (
                <div
                  key={company.id}
                  onClick={() => navigate(`/dashboard/${company.id}`)}
                  style={{
                    padding: '10px 14px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    color: '#1e293b',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#eef2ff'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                >
                  📁 {company.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfile;
